import { useCallback, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import { buildCreatePayload, buildEmptyCreateForm, validateCreateForm } from '../model/internal-mail.model.js';

export function useInternalMailCreate({ onSuccess, prefilledProfileUid = null } = {}) {
  const { token } = useAuth();
  const [form, setForm] = useState(() => buildEmptyCreateForm(prefilledProfileUid));
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const resetForm = useCallback(() => {
    setForm(buildEmptyCreateForm(prefilledProfileUid));
    setFormErrors({});
    setSubmitError('');
  }, [prefilledProfileUid]);

  const onFormChange = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFormErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const submit = useCallback(async (event) => {
    event.preventDefault();
    const errors = validateCreateForm(form);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const payload = buildCreatePayload(form);
      await adminApi.createInternalMessage(token, payload);
      resetForm();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setSubmitError(err.message || 'Failed to send message');
    } finally {
      setIsSubmitting(false);
    }
  }, [form, onSuccess, resetForm, token]);

  return {
    form,
    formErrors,
    isSubmitting,
    submitError,
    onFormChange,
    submit,
    resetForm,
  };
}
